import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';
import { Input } from '@/shared/ui/input';

export interface ViewDialogState {
  mode: 'create' | 'edit';
  viewId?: string;
  name: string;
  shared: boolean;
}

/**
 * Dialog pohledu (anatomie 01 §2) — vytvoření i úprava: povinný název + viditelnost
 * „Jen já" / „Sdílet s týmem". Enter potvrzuje, Esc zavírá; Uložit disabled bez názvu.
 */
export function ViewDialog({
  state,
  onClose,
  onSubmit,
}: {
  state: ViewDialogState | null;
  onClose: () => void;
  onSubmit: (data: { name: string; shared: boolean }) => Promise<boolean>;
}) {
  const { t } = useTranslation('listPage');
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state) {
      setName(state.name);
      setShared(state.shared);
      setSaving(false);
    }
  }, [state]);

  if (!state) {
    return null;
  }

  const canSave = name.trim().length > 0 && !saving;
  const submit = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    const ok = await onSubmit({ name: name.trim(), shared });
    setSaving(false);
    if (ok) {
      onClose();
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={state.mode === 'edit' ? t('views.dialogEditTitle') : t('views.dialogCreateTitle')}
      icon="save"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('views.dialogCancel')}
          </Button>
          <span className="flex-1" />
          <Button variant="primary" disabled={!canSave} onClick={() => void submit()}>
            {state.mode === 'edit' ? t('views.dialogSaveChanges') : t('views.dialogSave')}
          </Button>
        </>
      }
    >
      <div className="gap-phx-5 flex flex-col">
        <label className="gap-phx-2 flex flex-col">
          <span className="phxlist-caps text-phx-text-2">
            {t('views.dialogName')}
            <span aria-hidden className="text-phx-danger ml-0.5">
              *
            </span>
          </span>
          <Input
            autoFocus
            required
            value={name}
            placeholder={t('views.dialogNamePlaceholder')}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void submit();
              }
            }}
          />
        </label>
        <div className="gap-phx-2 flex flex-col">
          <span className="phxlist-caps text-phx-text-2">{t('views.dialogVisibility')}</span>
          <div className="gap-phx-3 grid grid-cols-2">
            <button
              type="button"
              className="phxlist-visopt"
              data-active={!shared || undefined}
              onClick={() => setShared(false)}
            >
              <span className="phxlist-visopt-ico">
                <Icon name="lock" size={14} />
              </span>
              <span>
                <b>{t('views.visibilityPrivate')}</b>
                <br />
                <span className="text-phx-xs text-phx-text-muted">
                  {t('views.visibilityPrivateHint')}
                </span>
              </span>
            </button>
            <button
              type="button"
              className="phxlist-visopt"
              data-active={shared || undefined}
              onClick={() => setShared(true)}
            >
              <span className="phxlist-visopt-ico">
                <Icon name="users" size={14} />
              </span>
              <span>
                <b>{t('views.visibilityTeam')}</b>
                <br />
                <span className="text-phx-xs text-phx-text-muted">
                  {t('views.visibilityTeamHint')}
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
